// finlytics-frontend/src/pages/dashboards/DCTrackerPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import styles from './DCTrackerPage.module.css';

const fmtCr = (v) => {
    if (v == null) return '—';
    if (v >= 1e5) return `₹${(v / 100000).toFixed(1)}L Cr`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K Cr`;
    return `₹${v.toLocaleString('en-IN')} Cr`;
};

const fmtPct = (v) => {
    if (v == null) return '—';
    return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};

const hyperscalerClass = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('microsoft') || n.includes('azure')) return styles.hyperMicrosoft;
    if (n.includes('google') || n.includes('gcp')) return styles.hyperGoogle;
    if (n.includes('aws') || n.includes('amazon')) return styles.hyperAWS;
    if (n.includes('oracle') || n.includes('oci')) return styles.hyperOracle;
    return styles.hyperDefault;
};

const categoryClass = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('capex')) return styles.catCapex;
    if (c.includes('ppa') || c.includes('solar')) return styles.catPpa;
    if (c.includes('customer')) return styles.catCustomer;
    if (c.includes('capacity')) return styles.catCapacity;
    if (c.includes('valuation')) return styles.catValuation;
    if (c.includes('regulatory')) return styles.catRegulatory;
    if (c.includes('financial')) return styles.catFinancial;
    return '';
};

const dotColor = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('capex')) return '#a855f7';
    if (c.includes('ppa') || c.includes('solar')) return '#22c55e';
    if (c.includes('customer')) return '#3b82f6';
    if (c.includes('capacity')) return '#f59e0b';
    if (c.includes('valuation')) return '#ec4899';
    if (c.includes('financial')) return '#06b6d4';
    return '#64748b';
};

const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiBar({ kpis }) {
    if (!kpis) return null;
    const items = [
        { label: 'Tracked Stocks', value: kpis.stock_count ?? '—' },
        { label: 'Total Capex', value: fmtCr(kpis.total_capex_cr), sub: 'committed' },
        { label: 'MW Operational', value: kpis.total_mw_operational != null ? `${kpis.total_mw_operational} MW` : '—', sub: `${kpis.total_mw_planned ?? 0} MW planned` },
        { label: 'Hyperscaler Deals', value: kpis.stocks_with_hyperscalers ?? '—', sub: 'companies' },
    ];
    return (
        <div className={styles.kpiBar}>
            {items.map((it, i) => (
                <div key={i} className={styles.kpiItem}>
                    <span className={styles.kpiLabel}>{it.label}</span>
                    <span className={styles.kpiValue}>{it.value}</span>
                    {it.sub && <span className={styles.kpiSub}>{it.sub}</span>}
                </div>
            ))}
        </div>
    );
}

function FilterBar({ sector, setSector, sortBy, setSortBy, sortDir, setSortDir, search, setSearch, sectors, total }) {
    return (
        <div className={styles.filterBar}>
            <select className={styles.filterSelect} value={sector} onChange={e => setSector(e.target.value)}>
                <option value="">All Sectors</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className={styles.filterSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="market_cap_cr">Market Cap</option>
                <option value="mw_operational">MW Operational</option>
                <option value="capex_committed_cr">Capex Committed</option>
                <option value="pe_ttm">P/E TTM</option>
                <option value="revenue_growth_pct">Revenue Growth</option>
                <option value="ticker">Ticker</option>
            </select>
            <button className={styles.sortDirBtn} onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>
                {sortDir === 'desc' ? '↓ Desc' : '↑ Asc'}
            </button>
            <input
                className={styles.filterSearch}
                placeholder="Search ticker or name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
            <span className={styles.filterCount}>{total} stock{total !== 1 ? 's' : ''}</span>
        </div>
    );
}

function MwProgressBar({ stock }) {
    const op = stock.mw_operational || 0;
    const uc = stock.mw_under_construction || 0;
    const total = stock.mw_planned_total || 0;
    if (total === 0 && op === 0) return <div className={styles.mwNotApplicable}>MW data not applicable</div>;
    const denom = Math.max(total, op + uc, 1);
    const opPct = (op / denom) * 100;
    const ucPct = (uc / denom) * 100;
    return (
        <div className={styles.mwSection}>
            <div className={styles.mwHeader}>
                <span className={styles.mwLabel}>MW Buildout</span>
                <span>
                    <span className={styles.mwValue}>{op + uc} / {total} MW</span>
                    {stock.mw_target_year && <span className={styles.mwTargetYear}>by {stock.mw_target_year}</span>}
                </span>
            </div>
            <div className={styles.mwBarTrack}>
                <div className={styles.mwBarOp} style={{ width: `${opPct}%` }} />
                <div className={styles.mwBarUc} style={{ left: `${opPct}%`, width: `${ucPct}%` }} />
            </div>
            <div className={styles.mwLegend}>
                <span><span className={styles.mwDot} style={{ background: '#22c55e' }} />Operational {op}</span>
                <span><span className={styles.mwDot} style={{ background: '#f59e0b' }} />Under Construction {uc}</span>
            </div>
        </div>
    );
}

function StockCard({ stock, onClick }) {
    const stale = daysSince(stock.price_last_synced);
    return (
        <div className={styles.stockCard} onClick={onClick} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') onClick(); }}>
            {/* Header */}
            <div className={styles.cardHeader}>
                <div>
                    <div className={styles.tickerRow}>
                        <span className={styles.ticker}>{stock.ticker}</span>
                        <span className={styles.exchangeBadge}>{stock.exchange}</span>
                        {stale != null && stale > 3 && <span className={styles.staleDaysBadge}>{stale}d stale</span>}
                    </div>
                    <div className={styles.entityName}>{stock.dc_entity_name || stock.name}</div>
                </div>
                <span className={styles.sectorPill}>{stock.sector}</span>
            </div>

            {/* MW Progress */}
            <MwProgressBar stock={stock} />

            {/* Mini cards */}
            <div className={styles.miniPanels}>
                <div className={styles.miniPanel}>
                    <div className={styles.miniLabel}>Capex</div>
                    <div className={styles.miniValue}>{stock.capex_committed_cr ? fmtCr(stock.capex_committed_cr) : '—'}</div>
                    {stock.capex_timeline && <div className={styles.miniSub}>{stock.capex_timeline}</div>}
                </div>
                <div className={styles.miniPanel}>
                    <div className={styles.miniLabel}>Solar PPA</div>
                    <div className={styles.miniValueGreen}>{stock.solar_ppa_mw ? `${stock.solar_ppa_mw} MW` : '—'}</div>
                    {stock.solar_ppa_partner && <div className={styles.miniSub}>{stock.solar_ppa_partner}</div>}
                </div>
            </div>

            {/* Hyperscaler tags */}
            {stock.hyperscaler_customers && stock.hyperscaler_customers.length > 0 && (
                <div className={styles.hyperscalerTags}>
                    {stock.hyperscaler_customers.map((h, i) => (
                        <span key={i} className={`${styles.hyperTag} ${hyperscalerClass(h)}`}>{h}</span>
                    ))}
                </div>
            )}

            {/* Valuation row */}
            <div className={styles.valuationRow}>
                <div className={styles.valMultiples}>
                    {stock.pe_ttm != null && <span className={styles.valChip}>P/E {stock.pe_ttm.toFixed(1)}</span>}
                    {stock.ev_ebitda != null && <span className={styles.valChip}>EV/E {stock.ev_ebitda.toFixed(1)}</span>}
                    {stock.pb_ratio != null && <span className={styles.valChip}>P/B {stock.pb_ratio.toFixed(1)}</span>}
                </div>
                {stock.revenue_growth_pct != null && (
                    <span className={`${styles.growthBadge} ${stock.revenue_growth_pct >= 20 ? styles.growthHigh : styles.growthNormal}`}>
                        Rev {fmtPct(stock.revenue_growth_pct)}
                    </span>
                )}
            </div>
        </div>
    );
}

function EventTimeline({ events }) {
    if (!events || events.length === 0) return <div className={styles.noEvents}>No events recorded yet.</div>;
    return (
        <div className={styles.timelineContainer}>
            {events.map((ev, i) => (
                <div key={i} className={styles.timelineEvent}>
                    <div className={styles.timelineDot} style={{ background: dotColor(ev.category) }} />
                    <div className={styles.timelineMeta}>
                        <span className={styles.timelineDate}>{ev.date ? new Date(ev.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                        <span className={`${styles.categoryBadge} ${categoryClass(ev.category)}`}>{ev.category}</span>
                        {ev.confidence === 'low' && <span className={styles.lowConfidence}>Low confidence</span>}
                    </div>
                    <div className={styles.timelineTitle}>{ev.headline || ev.title}</div>
                    {ev.detail && <div className={styles.timelineDetail}>{ev.detail}</div>}
                    {ev.source_url && <a className={styles.timelineSource} href={ev.source_url} target="_blank" rel="noopener noreferrer">Source ↗</a>}
                </div>
            ))}
        </div>
    );
}

function DetailDrawer({ stock, events, onClose }) {
    if (!stock) return null;
    const total = stock.mw_planned_total || Math.max(stock.mw_operational || 0, 1);
    return (
        <>
            <div className={styles.drawerOverlay} onClick={onClose} />
            <div className={styles.drawer}>
                {/* Header */}
                <div className={styles.drawerHeader}>
                    <div>
                        <div className={styles.drawerTicker}>{stock.ticker} <span className={styles.exchangeBadge}>{stock.exchange}</span></div>
                        <div className={styles.drawerName}>{stock.name}</div>
                        <div className={styles.drawerEntity}>{stock.dc_entity_name}</div>
                    </div>
                    <button className={styles.drawerCloseBtn} onClick={onClose}>✕</button>
                </div>

                <hr className={styles.divider} />

                {/* Valuation */}
                <div className={styles.sectionTitle}>Valuation</div>
                <div className={styles.detailGrid3}>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>Price</div>
                        <div className={styles.detailCellValueAmber}>₹{stock.current_price_inr?.toLocaleString('en-IN') ?? '—'}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>Market Cap</div>
                        <div className={styles.detailCellValue}>{fmtCr(stock.market_cap_cr)}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>P/E TTM</div>
                        <div className={styles.detailCellValue}>{stock.pe_ttm?.toFixed(1) ?? '—'}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>EV/EBITDA</div>
                        <div className={styles.detailCellValue}>{stock.ev_ebitda?.toFixed(1) ?? '—'}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>P/B Ratio</div>
                        <div className={styles.detailCellValue}>{stock.pb_ratio?.toFixed(1) ?? '—'}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>Rev Growth</div>
                        <div className={styles.detailCellValueGreen}>{fmtPct(stock.revenue_growth_pct)}</div>
                    </div>
                </div>

                <hr className={styles.divider} />

                {/* MW Buildout */}
                <div className={styles.sectionTitle}>MW Capacity Buildout</div>
                {[
                    { label: 'Operational', value: stock.mw_operational, color: '#22c55e' },
                    { label: 'Under Construction', value: stock.mw_under_construction, color: '#f59e0b' },
                    { label: 'Planned Total', value: stock.mw_planned_total, color: '#3b82f6' },
                ].map((row, i) => (
                    <div key={i} className={styles.mwBuildRow}>
                        <div className={styles.mwBuildLabel}>
                            <span style={{ color: '#94a3b8' }}>{row.label}</span>
                            <span style={{ color: row.color, fontWeight: 600 }}>{row.value ?? 0} MW</span>
                        </div>
                        <div className={styles.mwBuildBar}>
                            <div className={styles.mwBuildFill} style={{ width: `${Math.min(((row.value || 0) / total) * 100, 100)}%`, background: row.color }} />
                        </div>
                    </div>
                ))}
                {stock.mw_target_year && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Target: {stock.mw_target_year}</div>}

                <hr className={styles.divider} />

                {/* Capex */}
                <div className={styles.sectionTitle}>Capex</div>
                <div className={styles.detailGrid}>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>Committed</div>
                        <div className={styles.detailCellValueAmber}>{fmtCr(stock.capex_committed_cr)}</div>
                    </div>
                    <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>Timeline</div>
                        <div className={styles.detailCellValue}>{stock.capex_timeline || '—'}</div>
                    </div>
                </div>

                <hr className={styles.divider} />

                {/* Customers */}
                <div className={styles.sectionTitle}>Hyperscaler Customers</div>
                {stock.hyperscaler_customers && stock.hyperscaler_customers.length > 0 ? (
                    <div className={styles.hyperChipsDrawer}>
                        {stock.hyperscaler_customers.map((h, i) => (
                            <span key={i} className={`${styles.hyperTag} ${hyperscalerClass(h)}`}>{h}</span>
                        ))}
                    </div>
                ) : <div className={styles.noEvents}>No hyperscaler customers confirmed.</div>}
                {stock.enterprise_clients && <div className={styles.enterpriseClients}>Enterprise: {stock.enterprise_clients}</div>}

                <hr className={styles.divider} />

                {/* Solar PPA */}
                <div className={styles.sectionTitle}>Solar / Green Energy</div>
                {stock.solar_ppa_mw ? (
                    <>
                        <span className={styles.solarValue}>{stock.solar_ppa_mw} MW</span>
                        {stock.solar_ppa_target && <span className={styles.solarTarget}>Target: {stock.solar_ppa_target}</span>}
                        {stock.solar_ppa_partner && <div className={styles.solarDetails}>Partner: {stock.solar_ppa_partner}</div>}
                    </>
                ) : <div className={styles.noEvents}>No solar PPA data.</div>}

                <hr className={styles.divider} />

                {/* Strategy & Risks */}
                <div className={styles.sectionTitle}>DC Strategy</div>
                <div className={styles.strategyText}>{stock.dc_strategy || '—'}</div>

                <div className={styles.sectionTitle} style={{ marginTop: 14 }}>Key Risks</div>
                <div className={styles.riskText}>{Array.isArray(stock.key_risks) ? stock.key_risks.join(' · ') : stock.key_risks || '—'}</div>

                <hr className={styles.divider} />

                {/* Event Timeline */}
                <div className={styles.sectionTitle}>Event Timeline</div>
                <EventTimeline events={events} />

                <hr className={styles.divider} />

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {stock.source_url && <a className={styles.sourceLink} href={stock.source_url} target="_blank" rel="noopener noreferrer">Source ↗</a>}
                    {stock.last_updated && <span className={styles.lastUpdatedDrawer}>Updated {new Date(stock.last_updated).toLocaleDateString('en-IN')}</span>}
                </div>
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const SECTORS = ['Real Estate / DC', 'Telecom / DC', 'Conglomerate / DC', 'IT Services / DC', 'Hardware / DC'];

function ReviewQueuePanel() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusFilter, setStatusFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [actioningId, setActioningId] = useState(null);

    const fetchReview = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/dc/review', { params: { status: statusFilter } });
            setItems(res.data.items || []);
            setCounts(res.data.counts || {});
        } catch (err) {
            console.error('Review fetch error', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchReview(); }, [fetchReview]);

    const handleAction = useCallback(async (id, action) => {
        setActioningId(id);
        try {
            await apiClient.post(`/dc/review/${action}`, { id });
            fetchReview();
        } catch (err) {
            console.error(`Review ${action} failed`, err);
        } finally {
            setActioningId(null);
        }
    }, [fetchReview]);

    if (loading) return <div className={styles.emptyMsg}>Loading review queue…</div>;

    return (
        <div className={styles.reviewPanel}>
            <div className={styles.reviewFilterBar}>
                {['pending', 'approved', 'rejected', 'all'].map(s => (
                    <button
                        key={s}
                        className={`${styles.reviewFilterBtn} ${statusFilter === s ? styles.reviewFilterBtnActive : ''}`}
                        onClick={() => setStatusFilter(s)}
                    >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                        {counts[s] != null && s !== 'all' ? ` (${counts[s]})` : ''}
                    </button>
                ))}
            </div>
            {items.length === 0 ? (
                <div className={styles.emptyMsg}>No {statusFilter === 'all' ? '' : statusFilter} items in review queue.</div>
            ) : (
                items.map(item => (
                    <div key={item._id} className={styles.reviewItem}>
                        <div className={styles.reviewItemContent}>
                            <div>
                                <span className={styles.reviewTicker}>{item.ticker}</span>
                                <span className={`${styles.reviewConfidence} ${item.confidence === 'low' ? styles.confidenceLow : styles.confidenceMedium}`}>
                                    {item.confidence} confidence
                                </span>
                                {item.status !== 'pending' && (
                                    <span className={`${styles.reviewStatusBadge} ${item.status === 'approved' ? styles.statusApproved : styles.statusRejected}`} style={{ marginLeft: 6 }}>
                                        {item.status}
                                    </span>
                                )}
                            </div>
                            {item.fields_to_update && Object.keys(item.fields_to_update).length > 0 && (
                                <div className={styles.reviewFields}>
                                    {Object.entries(item.fields_to_update).map(([k, v]) => (
                                        <span key={k} className={styles.reviewFieldChip}>{k}: {typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
                                    ))}
                                </div>
                            )}
                            {item.hyperscalers_to_add && item.hyperscalers_to_add.length > 0 && (
                                <div className={styles.reviewFields}>
                                    {item.hyperscalers_to_add.map((h, i) => (
                                        <span key={i} className={`${styles.hyperTag} ${hyperscalerClass(h)}`}>{h}</span>
                                    ))}
                                </div>
                            )}
                            {item.event?.title && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Event: {item.event.title}</div>}
                            {item.source_url && <a className={styles.reviewSource} href={item.source_url} target="_blank" rel="noopener noreferrer">{item.source_url}</a>}
                            {item.created_at && <div className={styles.reviewDate}>{new Date(item.created_at).toLocaleString('en-IN')}</div>}
                        </div>
                        {item.status === 'pending' && (
                            <div className={styles.reviewActions}>
                                <button className={styles.approveBtn} disabled={actioningId === item._id} onClick={() => handleAction(item._id, 'approve')}>
                                    ✓ Approve
                                </button>
                                <button className={styles.rejectBtn} disabled={actioningId === item._id} onClick={() => handleAction(item._id, 'reject')}>
                                    ✕ Reject
                                </button>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
}

function ScanHistoryPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiClient.get('/dc/scan-history', { params: { limit: 50 } });
                setItems(res.data.items || []);
            } catch (err) {
                console.error('Scan history error', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className={styles.emptyMsg}>Loading scan history…</div>;
    if (items.length === 0) return <div className={styles.emptyMsg}>No scan history yet. Run a news scan to start.</div>;

    return (
        <div className={styles.historyPanel}>
            {items.map(item => (
                <div key={item._id} className={styles.historyItem}>
                    <div className={`${styles.historyIcon} ${item.extraction_successful ? styles.historySuccess : styles.historyFail}`}>
                        {item.extraction_successful ? '✓' : '✕'}
                    </div>
                    <div className={styles.historyContent}>
                        <div className={styles.historyTitle}>{item.title || item.url}</div>
                        <div className={styles.historyMeta}>
                            {item.source_type && <span>{item.source_type}</span>}
                            {item.created_at && <span>{new Date(item.created_at).toLocaleString('en-IN')}</span>}
                            {item.tickers_found && item.tickers_found.map(t => (
                                <span key={t} className={styles.historyTickerChip}>{t}</span>
                            ))}
                            {item.events_created > 0 && <span>{item.events_created} event{item.events_created > 1 ? 's' : ''}</span>}
                            {item.fields_updated && item.fields_updated.length > 0 && <span>{item.fields_updated.length} fields</span>}
                            {item.error_message && <span style={{ color: '#f87171' }}>{item.error_message}</span>}
                        </div>
                        {item.article_summary && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.article_summary}</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function DCTrackerPage() {
    const navigate = useNavigate();

    // data
    const [stocks, setStocks] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // filters
    const [sector, setSector] = useState('');
    const [sortBy, setSortBy] = useState('market_cap_cr');
    const [sortDir, setSortDir] = useState('desc');
    const [search, setSearch] = useState('');

    // detail drawer
    const [selectedTicker, setSelectedTicker] = useState(null);
    const [detailStock, setDetailStock] = useState(null);
    const [detailEvents, setDetailEvents] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    // price sync
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');

    // news scan
    const [scanning, setScanning] = useState(false);
    const [scanMsg, setScanMsg] = useState('');

    // tabs: stocks | review | history
    const [activeTab, setActiveTab] = useState('stocks');
    const [pendingCount, setPendingCount] = useState(0);

    // ---- Fetch stocks + KPIs ----
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (sector) params.sector = sector;
            if (sortBy) params.sort_by = sortBy;
            if (sortDir) params.sort_dir = sortDir;
            if (search) params.search = search;

            const [stocksRes, kpisRes] = await Promise.all([
                apiClient.get('/dc/stocks', { params }),
                apiClient.get('/dc/kpis'),
            ]);
            setStocks(stocksRes.data.stocks || []);
            setKpis(kpisRes.data);
        } catch (err) {
            console.error('DC fetch error', err);
            setError(err.response?.data?.error || err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [sector, sortBy, sortDir, search]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ---- Open detail drawer ----
    const openDetail = useCallback(async (ticker) => {
        setSelectedTicker(ticker);
        setDetailLoading(true);
        try {
            const res = await apiClient.get(`/dc/stocks/${ticker}`);
            setDetailStock(res.data.stock);
            setDetailEvents(res.data.events || []);
        } catch (err) {
            console.error('Detail fetch error', err);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const closeDetail = useCallback(() => {
        setSelectedTicker(null);
        setDetailStock(null);
        setDetailEvents([]);
    }, []);

    // ---- Price sync ----
    const handleSync = useCallback(async () => {
        setSyncing(true);
        setSyncMsg('');
        try {
            const res = await apiClient.post('/dc/refresh');
            const updated = res.data.updated?.length || 0;
            const errors = res.data.errors?.length || 0;
            setSyncMsg(`${updated} updated${errors ? `, ${errors} errors` : ''}`);
            fetchData();
        } catch (err) {
            setSyncMsg('Sync failed');
        } finally {
            setSyncing(false);
        }
    }, [fetchData]);

    // ---- News scan ----
    const handleScan = useCallback(async () => {
        setScanning(true);
        setScanMsg('');
        try {
            const res = await apiClient.post('/dc/scan');
            const d = res.data;
            setScanMsg(`${d.articles_processed || 0} processed, ${d.auto_applied || 0} applied, ${d.queued_for_review || 0} queued`);
            fetchData();
            // Refresh pending count
            try {
                const rr = await apiClient.get('/dc/review', { params: { status: 'pending', limit: 1 } });
                setPendingCount(rr.data.counts?.pending || 0);
            } catch (_) { }
        } catch (err) {
            setScanMsg('Scan failed — check console');
            console.error('DC scan error', err);
        } finally {
            setScanning(false);
        }
    }, [fetchData]);

    // ---- Fetch pending review count on mount ----
    useEffect(() => {
        (async () => {
            try {
                const res = await apiClient.get('/dc/review', { params: { status: 'pending', limit: 1 } });
                setPendingCount(res.data.counts?.pending || 0);
            } catch (_) { }
        })();
    }, []);

    // ---- Derived ----
    const activeSectors = useMemo(() => {
        const s = new Set(stocks.map(st => st.sector).filter(Boolean));
        return SECTORS.filter(sec => s.has(sec));
    }, [stocks]);

    // ---- Render ----
    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate('/dashboards')} aria-label="Dashboards">← Dashboards</button>
                <div className={styles.headerText}>
                    <h1 className={styles.headerTitle}>INDIA DC INFRASTRUCTURE TRACKER</h1>
                    <p className={styles.headerSubtitle}>9 listed stocks · data-centre buildout theme</p>
                </div>
                <div className={styles.headerRight}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className={styles.scanBtn} disabled={scanning} onClick={handleScan}>
                            {scanning ? 'Scanning…' : '📡 Scan News'}
                        </button>
                        <button className={styles.syncBtn} disabled={syncing} onClick={handleSync}>
                            {syncing ? 'Syncing…' : '⟳ Refresh Prices'}
                        </button>
                    </div>
                    {scanMsg && <span className={styles.scanMsg}>{scanMsg}</span>}
                    {syncMsg && <span className={styles.syncMsg}>{syncMsg}</span>}
                </div>
            </div>

            <div className={styles.content}>
                {/* KPI Bar */}
                <KpiBar kpis={kpis} />

                {/* Tab Bar */}
                <div className={styles.tabBar}>
                    <button className={`${styles.tab} ${activeTab === 'stocks' ? styles.tabActive : ''}`} onClick={() => setActiveTab('stocks')}>
                        Stocks
                    </button>
                    <button className={`${styles.tab} ${activeTab === 'review' ? styles.tabActive : ''}`} onClick={() => setActiveTab('review')}>
                        Review Queue
                        {pendingCount > 0 && <span className={styles.tabBadge}>{pendingCount}</span>}
                    </button>
                    <button className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`} onClick={() => setActiveTab('history')}>
                        Scan History
                    </button>
                </div>

                {/* Tab Content: Stocks */}
                {activeTab === 'stocks' && (
                    <>
                        <FilterBar
                            sector={sector} setSector={setSector}
                            sortBy={sortBy} setSortBy={setSortBy}
                            sortDir={sortDir} setSortDir={setSortDir}
                            search={search} setSearch={setSearch}
                            sectors={activeSectors}
                            total={stocks.length}
                        />
                        {loading ? (
                            <div className={styles.loadingGrid}>
                                {[...Array(6)].map((_, i) => <div key={i} className={styles.skeletonCard} />)}
                            </div>
                        ) : error ? (
                            <div className={styles.errorMsg}>{error}</div>
                        ) : stocks.length === 0 ? (
                            <div className={styles.emptyMsg}>No stocks match your filters.</div>
                        ) : (
                            <div className={styles.stockGrid}>
                                {stocks.map(s => (
                                    <StockCard key={s.ticker} stock={s} onClick={() => openDetail(s.ticker)} />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* Tab Content: Review Queue */}
                {activeTab === 'review' && <ReviewQueuePanel />}

                {/* Tab Content: Scan History */}
                {activeTab === 'history' && <ScanHistoryPanel />}
            </div>

            {/* Detail Drawer */}
            {selectedTicker && (
                detailLoading ? null : (
                    <DetailDrawer stock={detailStock} events={detailEvents} onClose={closeDetail} />
                )
            )}
        </div>
    );
}
